import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowBack, FilterAlt } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { ResourceListView } from '../components/resource-list-view';
import { useListResourceTypesQuery } from '../store/hooks';

const PER_PAGE = 25;

export default function ResourceListPage() {
  const { resourceType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const organizationId = searchParams.get('organization_id') ?? undefined;
  const patientId = searchParams.get('patient_id') ?? undefined;
  const providerId = searchParams.get('provider_id') ?? undefined;
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  // The type's capability decides which owner filters are even applicable
  // (e.g. patient_medication has no provider dimension), so we only offer
  // the inputs the backend will honor.
  const { data: catalog } = useListResourceTypesQuery();
  const capability = catalog?.items?.find(
    r => r.resource_type === resourceType,
  )?.capability;
  const canPatient = capability?.patient ?? false;
  const canProvider = capability?.user ?? false;

  const [patientInput, setPatientInput] = useState(patientId ?? '');
  const [providerInput, setProviderInput] = useState(providerId ?? '');

  const applyFilters = () => {
    const next = new URLSearchParams(searchParams);
    const set = (key: string, value: string) => {
      if (value.trim()) next.set(key, value.trim());
      else next.delete(key);
    };
    if (canPatient) set('patient_id', patientInput);
    if (canProvider) set('provider_id', providerInput);
    next.delete('page'); // new filter → back to page 1
    setSearchParams(next);
  };

  const onPageChange = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  // Logical "up": back to the Resource Explorer (now URL-addressable), not
  // raw history — so this works even on a deep-linked list.
  const backToExplorer = () => {
    if (organizationId) {
      navigate(
        `/admin-v2/organization/${organizationId}?tab=resource-explorer`,
      );
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Box sx={{ mb: 3 }}>
          <Button onClick={backToExplorer} startIcon={<ArrowBack />}>
            Back to explorer
          </Button>
        </Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Resource Explorer
        </Typography>
        {resourceType ? (
          <Card>
            <CardHeader
              title={resourceType}
              subheader="Owner-scoped list via the common interface — filter by the applicable owner, then open a row for the full record."
            />
            <CardContent>
              {(canPatient || canProvider) && (
                <Box
                  sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}
                >
                  {canPatient && (
                    <TextField
                      size="small"
                      label="Patient id"
                      value={patientInput}
                      onChange={e => setPatientInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') applyFilters();
                      }}
                    />
                  )}
                  {canProvider && (
                    <TextField
                      size="small"
                      label="Provider id"
                      value={providerInput}
                      onChange={e => setProviderInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') applyFilters();
                      }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<FilterAlt />}
                    onClick={applyFilters}
                  >
                    Apply
                  </Button>
                </Box>
              )}
              <ResourceListView
                resourceType={resourceType}
                organizationId={organizationId}
                patientId={canPatient ? patientId : undefined}
                providerId={canProvider ? providerId : undefined}
                page={page}
                perPage={PER_PAGE}
                onPageChange={onPageChange}
              />
            </CardContent>
          </Card>
        ) : (
          <Typography color="error">Missing resource type.</Typography>
        )}
      </main>
    </div>
  );
}
